import { Select as MantineSelect, SelectProps, useMantineTheme } from '@mantine/core';
import { forwardRef, CSSProperties } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';
import classes from './Select.module.css';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedSelectProps extends SelectProps {
  gradientFocus?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const Select = forwardRef<HTMLInputElement, ThemedSelectProps>(
  ({ gradientFocus = true, gradientVariant = 'primary', useColorScheme = true, styles, classNames, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    if (!gradientFocus) {
      return (
        <MantineSelect
          ref={ref}
          styles={{
            ...styles,
            label: {
              textAlign: 'left !important' as any,
              ...(styles as any)?.label,
            },
            input: {
              paddingLeft: rest.leftSection ? '40px' : undefined,
              paddingRight: rest.rightSection ? '40px' : undefined,
              ...(styles as any)?.input,
            },
          }}
          {...rest}
        />
      );
    }

    const cssVars = {
      '--select-focus-color': color,
      '--select-focus-shadow': strongFocus ? `0 0 0 3px ${color}50` : `0 0 0 2px ${color}30`,
      '--select-focus-outline': strongFocus ? `2px solid ${color}` : 'none',
      '--select-focus-outline-offset': strongFocus ? '2px' : '0',
      '--select-option-hover-bg': `${color}20`,
    } as CSSProperties;

    return (
      <MantineSelect
        ref={ref}
        style={cssVars}
        classNames={{
          input: classes.input,
          option: classes.option,
          ...classNames,
        }}
        styles={{
          ...styles,
          label: {
            textAlign: 'left !important' as any,
            ...(styles as any)?.label,
          },
          input: {
            paddingLeft: rest.leftSection ? '40px' : undefined,
            paddingRight: rest.rightSection ? '40px' : undefined,
            transition: reducedMotion ? 'none' : 'border-color 0.2s ease, box-shadow 0.2s ease',
            ...(styles as any)?.input,
          },
          option: {
            ...(styles as any)?.option,
          },
        }}
        {...rest}
      />
    );
  }
);

Select.displayName = 'Select';

export default Select;
